import React from "react";
import { Redirect } from "react-router";
import { Route, Switch, useLocation, matchPath, useHistory } from "react-router-dom";
import { StayCompliantDataRetentionPage } from "../pages/data-map/StayCompliantDataRetentionPage";
import { StayCompliantFinancialIncentivesPage } from "../components/privacyCenter/StayCompliantFinancialIncentivesPage";
import { StayCompliantIntroEditScreen } from "../components/privacyCenter/StayCompliantIntroEditScreen";
import { StayCompliantPublishNoticesPage } from "../components/privacyCenter/StayCompliantPublishNoticesPage";
import { useSetApiMode } from "../hooks/useSetApiMode";
import { AcceptInvitation } from "../pages/auth/AcceptInvitationPage";
import { ConsumerDetailPage } from "../pages/consumer/ConsumerDetailPage";
import { AddEmployeeDataRecipientsPage } from "../pages/data-map/AddEmployeeDataRecipientsPage";
import { CollectionGroupDetailPage } from "../pages/data-map/CollectionGroupDetailPage";
import { CollectionGroupNewPage } from "../pages/data-map/CollectionGroupNewPage";
import { CollectedPicForGroupPage } from "../pages/data-map/components/workflows/create-collection-group/CollectedPicForGroupPage";
import { DataReceiptForGroupPage } from "../pages/data-map/components/workflows/create-collection-group/DataReceiptForGroupPage";
import { DataRecipientAssociationForGroupPage } from "../pages/data-map/components/workflows/create-collection-group/DataRecipientAssociationForGroupPage";
import { DataSourceAssociationForGroupPage } from "../pages/data-map/components/workflows/create-collection-group/DataSourceAssociationForGroupPage";
import { DisclosuresForGroupPage } from "../pages/data-map/components/workflows/create-collection-group/DisclosuresForGroupPage";
import { ClassificationForDataRecipientPage } from "../pages/data-map/components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import { CollectionGroupAssociationForDataRecipientPage } from "../pages/data-map/components/workflows/create-data-recipient/CollectionGroupAssociationForDataRecipientPage";
import { DisclosureForDataRecipientPage } from "../pages/data-map/components/workflows/create-data-recipient/DisclosureForDataRecipientPage";
import { CollectionGroupAssociationPage } from "../pages/data-map/components/workflows/create-data-source/CollectionGroupAssociationPage";
import { DataReceiptForSourcePage } from "../pages/data-map/components/workflows/create-data-source/DataReceiptForSourcePage";
import { DataMapOverviewPage } from "../pages/data-map/DataMapOverviewPage";
import { DataRecipientDetailPage } from "../pages/data-map/DataRecipientDetailPage";
import { DataRecipientNewPage } from "../pages/data-map/DataRecipientNewPage";
import { DataSourceDetailPage } from "../pages/data-map/DataSourceDetailPage";
import { DataSourceNewPage } from "../pages/data-map/DataSourceNewPage";
import { LawfulBasesDetailPage } from "../pages/data-map/LawfulBasesDetailPage";
import { EmailTemplateSettings } from "../pages/organization/EmailTemplateSettingsPage";
import { OrganizationSettings } from "../pages/organization/OrganizationSettingsPage";
import { UserSettings } from "../pages/organization/UserSettingsPage";
import { WebsiteAuditPage } from "../pages/organization/WebsiteAuditPage";
import { PrintPrivacyNoticeReview } from "../pages/print/PrintPrivacyNoticeReviewPage";
import { RequestResultsPage } from "../pages/records/RequestResultsPage";
import { CGInstructionsPage } from "../pages/requestInstructions/CGInstructionsPage";
import { DeleteInstructionsPage } from "../pages/requestInstructions/DeleteInstructionsPage";
import { KnowInstructionsPage } from "../pages/requestInstructions/KnowInstructionsPage";
import { OptOutInstructionsPage } from "../pages/requestInstructions/OptOutInstructionsPage";
import { RequestInstructionsHome } from "../pages/requestInstructions/RequestInstructionsHome";
import { Detail as RequestDetail } from "../pages/requests/RequestDetailPage";
import { Listing as RequestListing } from "../pages/requests/RequestListingPage";
import { KnowledgeCenterPage } from "../pages/stay-compliant/KnowledgeCenterPage";
import { AddEditPrivacySectionPage } from "../pages/stay-compliant/StayCompliantNoticesAddEditPrivacySectionPage";
import { PrivacyCenter as StayCompliantNotices } from "../pages/stay-compliant/StayCompliantPrivacyCenterPage";
import { Listing as TaskListing } from "../pages/tasks/TaskListingPage";
import { FourOhFourRoutes } from "./FourOhFourRoutes";
import { Routes } from "./routes";
import { EditHrRetentionPolicyPage } from "../pages/stay-compliant/hr-center/EditHrRetentionPolicyPage";
import { PrivacyCenterTheme } from "../pages/organization/PrivacyCenterThemePage";
import { PrivacyCenterPreviewPopupPage } from "../pages/organization/PrivacyCenterPreviewPopupPage";
import { MyDocumentsPage } from "../pages/stay-compliant/documents/MyDocumentsPage";
import { DPAPageCurrent } from "../pages/stay-compliant/documents/DPAPageCurrent";
import { DPAPagePending } from "../pages/stay-compliant/documents/DPAPagePending";
import { DPAPageHistory } from "../pages/stay-compliant/documents/DPAPageHistory";
import { DPAAddApprovalParties } from "../pages/stay-compliant/documents/DPAAddApprovalParties";
import { DPAGatherSignatures } from "../pages/stay-compliant/documents/DPAGatherSignatures";
import { DPAWrite } from "../pages/stay-compliant/documents/DPAWrite";
import { PrivacyCenterConsentBanner } from "../pages/organization/PrivacyCenterConsentBannerPage";
import { LimitInstructionsPage } from "../pages/requestInstructions/LimitInstructionsPage";
import { PrivacyCenterIntegrations } from "../pages/organization/PrivacyCenterIntegrationsPage";
import { EventPulseLaunchPage } from "../pages/organization/EventPulseLaunchPage";
import { ShopifyIntegrationPage } from "../pages/integrations/ShopifyIntegrationPage";
import { PrivacyCenterDashboard } from "../pages/stay-compliant/PrivacyCenterDashboard";
import WorkflowsDashboardPage from "../pages/stay-compliant/WorkflowsDashboardPage";
import { USStateNoticesPage } from "../pages/stay-compliant/privacy-center/us-state-notices/USStateNoticesPage";
import { OrganizationBillingPage } from "../pages/organization/OrganizationBillingPage";
import MainAppDashboard from "../pages/dashboard/MainAppDashboard";
import { ProfilngOptOutInstructionsPage } from "../pages/requestInstructions/ProfilingOptOutInstructionsPage";
import { UserProfilePage } from "../pages/user/UserProfilePage";
import { HRCenterDashboardPage } from "../pages/stay-compliant/hr-center/HRCenterDashboardPage";
import { CSComplianceDashboardPage } from "../pages/admin/CSComplianceDashboardPage";
import { HRNoticesInstructionsPage } from "../pages/stay-compliant/hr-center/HRNoticesInstructionsPage";
import { RoPAPageHome } from "../pages/stay-compliant/documents/ropa/RoPAPageHome";
import { RoPAPageHistory } from "../pages/stay-compliant/documents/ropa/RoPAPageHistory";
import { RoPAPageEdit } from "../pages/stay-compliant/documents/ropa/RoPAPageEdit";
import { HRDataRetentionPolicyDocuments } from "../pages/stay-compliant/documents/HRDataRetentionPolicyDocuments";
import { DataStorageSettingsPage } from "../pages/stay-compliant/workflows/DataStorageSettingsPage";
import { AccessRequestInstructionsPage } from "../pages/requestInstructions/AccessRequestInstructionsPage";
import { EmployeeCollectionPage } from "../pages/stay-compliant/hr-center/hr-data-map/EmployeeCollectionPage";
import { EmployeeRecipientsPage } from "../pages/stay-compliant/hr-center/hr-data-map/EmployeeRecipientsPage";
import { EmployeeDisclosuresPage } from "../pages/stay-compliant/hr-center/hr-data-map/EmployeeDisclosuresPage";
import { EmployeeSourcesPage } from "../pages/stay-compliant/hr-center/hr-data-map/EmployeeSourcesPage";
import { EmployeeDataAccessPage } from "../pages/stay-compliant/hr-center/hr-data-map/EmployeeDataAccessPage";
import { EmployeeExceptionsPage } from "../pages/stay-compliant/hr-center/hr-data-map/EmployeeExceptionsPage";
import { HRDataMapRootPage } from "../pages/stay-compliant/hr-center/hr-data-map/HRDataMapRootPage";
import { PrivacyCenterDetailEmailPage } from "../pages/requests/PrivacyCenterDetailEmailPage";
import { RequestTutorialsPage } from "../pages/requests/RequestTutorialsPage";
import { RecordLookupPage } from "../pages/requests/RecordLookupPage";
import { DSARFormsPage } from "../pages/stay-compliant/privacy-center/DSARForms";
import { AuditLogsPage } from "../pages/stay-compliant/documents/auditLogs/AuditLogsPage";
import { MyFilesPage } from "../pages/stay-compliant/documents/myFiles/MyFilesPage";
import { PrivacyPolicyDocumentsPage } from "../pages/stay-compliant/documents/privacyPolicies/PrivacyPolicyDocumentsPage";
import { DataMapDashboardPage } from "../pages/data-map/DataMapDashboardPage";
import { BusinessSurveySettingsPage } from "../pages/stay-compliant/BusinessSurveySettingsPage";
import { VendorDocumentationPage } from "../pages/data-map/vendor-documentation/VendorDocumentationPage";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { Button } from "@truevault/aquilo";

// Hard-coded allowlist of pages that HR users can access
const HR_USER_ALLOWLIST = [
  Routes.root,
  Routes.login,
  Routes.reset,
  ...Object.values(Routes.auth).flat(),
  Routes.user.profile,

  Routes.knowledgeCenter.root,

  // Include all HR Center routes
  ...Object.values(Routes.hrCenter).flatMap((val) =>
    typeof val === "object" ? Object.values(val) : val,
  ),

  Routes.requests.active,
  Routes.requests.closed,
  Routes.requests.pending,
  Routes.requests.detail,
  Routes.records.recordLookup,
  Routes.records.requestResults,
];

export const StayCompliantPageRouter = ({
  isHrUser,
  setShowingCustomBanner,
}: {
  isHrUser: boolean;
  setShowingCustomBanner: (value: boolean) => void;
}) => {
  useSetApiMode("Live", "StayCompliantPageRouter");
  const location = useLocation();

  // Check HR user allowlist of pages
  if (
    isHrUser &&
    !HR_USER_ALLOWLIST.some((path) => matchPath(location.pathname, { path, exact: true }))
  ) {
    return <HrAccessDeniedPage />;
  }

  // HR users should always go directly to the HR Center
  const defaultRedirectRoute = isHrUser ? Routes.hrCenter.root : Routes.dashboard.root;
  return (
    <Switch>
      <Route
        exact
        path={Routes.requests.active}
        key={"active-requests"}
        render={(props) => <RequestListing {...props} filter="active" />}
      />
      <Route
        exact
        path={Routes.requests.closed}
        key={"closed-requests"}
        render={(props) => <RequestListing {...props} filter="complete" />}
      />
      <Route
        exact
        path={Routes.requests.pending}
        key={"pending-requests"}
        render={(props) => <RequestListing {...props} filter="pending" />}
      />
      <Route exact path={Routes.requests.tutorials} component={RequestTutorialsPage} />
      <Route exact path={Routes.dashboard.root} component={MainAppDashboard} />
      <Route
        exact
        path={Routes.tasks.open}
        render={(props) => <TaskListing {...props} filter="open" />}
      />
      <Route
        exact
        path={Routes.tasks.completed}
        render={(props) => <TaskListing {...props} filter="completed" />}
      />
      <Route
        exact
        path={Routes.requests.privacyEmailSettings}
        component={PrivacyCenterDetailEmailPage}
      />
      <Route exact path={Routes.requests.detail} component={RequestDetail} />
      <Route exact path={Routes.records.recordLookup} component={RecordLookupPage} />
      <Route
        exact
        path={Routes.records.websiteConsentRecords}
        component={PrivacyCenterConsentBanner}
      />
      <Route
        exact
        path={Routes.records.consumerConsentLookup}
        component={PrivacyCenterConsentBanner}
      />
      <Route exact path={Routes.records.requestResults} component={RequestResultsPage} />
      <Route exact path={Routes.consumer.detail} component={ConsumerDetailPage} />
      <Route
        exact
        path={Routes.organization.settings.businessSurvey}
        component={BusinessSurveySettingsPage}
      />
      <Route exact path={Routes.organization.settings.root} component={OrganizationSettings} />
      <Route exact path={Routes.organization.settings.users} component={UserSettings} />
      <Route exact path={Routes.privacyCenter.theme}>
        <PrivacyCenterTheme setShowingCustomBanner={setShowingCustomBanner} />
      </Route>
      <Route exact path={Routes.privacyCenter.usStates} component={USStateNoticesPage} />
      <Route exact path={Routes.privacyCenter.dsarForms} component={DSARFormsPage} />
      <Route
        exact
        path={Routes.privacyCenter.consentBanner}
        component={PrivacyCenterConsentBanner}
      />
      <Route exact path={Routes.privacyCenter.integrations} component={PrivacyCenterIntegrations} />
      <Route
        exact
        path={Routes.privacyCenter.previewPopup}
        component={PrivacyCenterPreviewPopupPage}
      />
      <Route
        exact
        path={Routes.organization.settings.billing}
        component={OrganizationBillingPage}
      />
      <Route exact path={Routes.organization.eventPulse} component={EventPulseLaunchPage} />
      <Route exact path={Routes.hrCenter.root} component={HRCenterDashboardPage} />
      <Route exact path={Routes.hrCenter.hrDataMap.root} component={HRDataMapRootPage} />
      <Route
        exact
        path={Routes.hrCenter.hrDataMap.employeeCollection}
        component={EmployeeCollectionPage}
      />
      <Route
        exact
        path={Routes.hrCenter.hrDataMap.employeeRecipients}
        component={EmployeeRecipientsPage}
      />
      <Route
        exact
        path={Routes.hrCenter.hrDataMap.employeeDisclosures}
        component={EmployeeDisclosuresPage}
      />
      <Route
        exact
        path={Routes.hrCenter.hrDataMap.employeeSources}
        component={EmployeeSourcesPage}
      />
      <Route
        exact
        path={Routes.hrCenter.hrDataMap.employeeDataAccess}
        component={EmployeeDataAccessPage}
      />
      <Route
        exact
        path={Routes.hrCenter.hrDataMap.employeeExceptions}
        component={EmployeeExceptionsPage}
      />
      <Route
        exact
        path={Routes.hrCenter.editHrRetentionPolicy}
        component={EditHrRetentionPolicyPage}
      />
      <Route exact path={Routes.hrCenter.hrNotices} component={HRNoticesInstructionsPage} />
      <Route
        exact
        path={Routes.organization.requestHandling.base}
        component={RequestInstructionsHome}
      />
      <Route
        exact
        path={Routes.organization.requestHandling.consumerGroup}
        component={CGInstructionsPage}
      />
      <Route
        exact
        path={Routes.organization.requestHandling.requestToKnow}
        component={KnowInstructionsPage}
      />
      <Route
        exact
        path={Routes.organization.requestHandling.requestToDelete}
        component={DeleteInstructionsPage}
      />
      <Route
        exact
        path={Routes.organization.requestHandling.accessRequest}
        component={AccessRequestInstructionsPage}
      />
      <Route
        exact
        path={Routes.organization.requestHandling.requestToOptOut}
        component={OptOutInstructionsPage}
      />
      <Route
        exact
        path={Routes.organization.requestHandling.requestToProfilingOptOut}
        component={ProfilngOptOutInstructionsPage}
      />
      <Route
        exact
        path={Routes.organization.requestHandling.requestToLimit}
        component={LimitInstructionsPage}
      />
      <Route exact path={Routes.dataMap.root} component={DataMapOverviewPage} />
      <Route exact path={Routes.dataMap.dashboard} component={DataMapDashboardPage} />
      <Route exact path={Routes.dataMap.dataRetention} component={StayCompliantDataRetentionPage} />
      <Route exact path={Routes.dataMap.vendorDocumentation} component={VendorDocumentationPage} />
      <Route
        exact
        path={Routes.dataMap.dataRecipient.employee}
        component={AddEmployeeDataRecipientsPage}
      />
      <Route exact path={Routes.dataMap.lawfulBases.detail} component={LawfulBasesDetailPage} />
      <Route exact path={Routes.dataMap.collectionGroup.new} component={CollectionGroupNewPage} />
      <Route
        exact
        path={Routes.dataMap.collectionGroup.workflow.collection}
        component={CollectedPicForGroupPage}
      />
      <Route
        exact
        path={Routes.dataMap.collectionGroup.workflow.dataRecipientAssociation}
        component={DataRecipientAssociationForGroupPage}
      />
      <Route
        exact
        path={Routes.dataMap.collectionGroup.workflow.disclosure}
        component={DisclosuresForGroupPage}
      />
      <Route
        exact
        path={Routes.dataMap.collectionGroup.workflow.sourceAssociation}
        component={DataSourceAssociationForGroupPage}
      />
      <Route
        exact
        path={Routes.dataMap.collectionGroup.workflow.receipt}
        component={DataReceiptForGroupPage}
      />
      <Route
        exact
        path={Routes.dataMap.collectionGroup.detail}
        component={CollectionGroupDetailPage}
      />
      <Route exact path={Routes.dataMap.dataRecipient.new} component={DataRecipientNewPage} />
      <Route
        exact
        path={Routes.dataMap.dataRecipient.workflow.classification}
        component={ClassificationForDataRecipientPage}
      />
      <Route
        exact
        path={Routes.dataMap.dataRecipient.workflow.groupAssociation}
        component={CollectionGroupAssociationForDataRecipientPage}
      />
      <Route
        exact
        path={Routes.dataMap.dataRecipient.workflow.disclosure}
        component={DisclosureForDataRecipientPage}
      />
      <Route exact path={Routes.dataMap.dataRecipient.detail} component={DataRecipientDetailPage} />
      <Route exact path={Routes.dataMap.dataSource.new} component={DataSourceNewPage} />
      <Route
        exact
        path={Routes.dataMap.dataSource.workflow.groupAssociation}
        component={CollectionGroupAssociationPage}
      />
      <Route
        exact
        path={Routes.dataMap.dataSource.workflow.receipt}
        component={DataReceiptForSourcePage}
      />
      <Route exact path={Routes.dataMap.dataSource.detail} component={DataSourceDetailPage} />
      <Route exact path={Routes.print.privacyNotices.caPrivacyNotice}>
        <PrintPrivacyNoticeReview noticeType="CA_PRIVACY_NOTICE" />
      </Route>
      <Route exact path={Routes.print.privacyNotices.optOutPrivacyNotice}>
        <PrintPrivacyNoticeReview noticeType="OPT_OUT_PRIVACY_NOTICE" />
      </Route>
      <Route exact path={Routes.print.privacyNotices.consumerGroup}>
        <PrintPrivacyNoticeReview noticeType="CONSUMER_GROUP" />
      </Route>
      <Route exact path={Routes.cookies.root} component={PrivacyCenterConsentBanner} />{" "}
      {/* The cookie scanner is now a tab on the Website Consent page. */}
      <Route exact path={Routes.workflows.dashboard} component={WorkflowsDashboardPage} />
      <Route exact path={Routes.user.profile} component={UserProfilePage} />
      <Route
        exact
        path={Routes.admin.csComplianceDashboard}
        component={CSComplianceDashboardPage}
      />
      <Route
        exact
        path={Routes.workflows.dataStorageSettings}
        component={DataStorageSettingsPage}
      />
      <Route exact path={Routes.workflows.emailTemplates} component={EmailTemplateSettings} />
      <Route exact path={Routes.trueScan.root} component={PrivacyCenterConsentBanner} />
      <Route exact path={Routes.websiteAudit.root} component={WebsiteAuditPage} />
      <Route exact path={Routes.privacyCenter.root} component={PrivacyCenterDashboard} />
      <Route exact path={Routes.privacyCenter.privacyPolicy} component={StayCompliantNotices} />
      <Route exact path={Routes.privacyCenter.dashboard} component={PrivacyCenterDashboard} />
      <Route
        exact
        path={Routes.privacyCenter.editPrivacyIntro}
        component={StayCompliantIntroEditScreen}
      />
      <Route
        exact
        path={Routes.privacyCenter.financialIncentives}
        component={StayCompliantFinancialIncentivesPage}
      />
      <Route
        exact
        path={Routes.privacyCenter.publish}
        component={StayCompliantPublishNoticesPage}
      />
      <Route
        exact
        path={Routes.privacyCenter.addPrivacySection}
        component={AddEditPrivacySectionPage}
        // @ts-ignore
        render={(props) => <AddEditPrivacySectionPage {...props} add={true} />}
      />
      <Route
        exact
        path={Routes.privacyCenter.editPrivacySection}
        component={AddEditPrivacySectionPage}
        // @ts-ignore
        render={(props) => <AddEditPrivacySectionPage {...props} add={false} />}
      />
      <Route exact path={Routes.notices.root}>
        <Redirect to={Routes.privacyCenter.root} />
      </Route>
      <Route exact path={Routes.notices.editPrivacyIntro}>
        <Redirect to={Routes.privacyCenter.editPrivacyIntro} />
      </Route>
      <Route exact path={Routes.notices.dataRetention}>
        <Redirect to={Routes.dataMap.dataRetention} />
      </Route>
      <Route exact path={Routes.privacyCenter.dataRetention}>
        <Redirect to={Routes.dataMap.dataRetention} />
      </Route>
      <Route exact path={Routes.notices.financialIncentives}>
        <Redirect to={Routes.privacyCenter.financialIncentives} />
      </Route>
      <Route exact path={Routes.notices.publish}>
        <Redirect to={Routes.privacyCenter.publish} />
      </Route>
      <Route exact path={Routes.notices.addPrivacySection}>
        <Redirect to={Routes.privacyCenter.addPrivacySection} />
      </Route>
      <Route exact path={Routes.notices.editPrivacySection}>
        <Redirect to={Routes.privacyCenter.editPrivacySection} />
      </Route>
      <Route exact path={Routes.knowledgeCenter.root} component={KnowledgeCenterPage} />
      <Route exact path={Routes.documents.root} component={MyDocumentsPage} />
      <Route exact path={Routes.documents.dataProtectionAssessments} component={DPAPageCurrent} />
      <Route exact path={Routes.documents.pendingDPA} component={DPAPagePending} />
      <Route exact path={Routes.documents.historyDPA} component={DPAPageHistory} />
      <Route exact path={Routes.documents.dataProtectionAssessment.write} component={DPAWrite} />
      <Route
        exact
        path={Routes.documents.dataProtectionAssessment.addApprovalParties}
        component={DPAAddApprovalParties}
      />
      <Route
        exact
        path={Routes.documents.dataProtectionAssessment.gatherSignatures}
        component={DPAGatherSignatures}
      />
      <Route
        exact
        path={Routes.documents.recordOfProcessingActivities.home}
        component={RoPAPageHome}
      />
      <Route
        exact
        path={Routes.documents.recordOfProcessingActivities.history}
        component={RoPAPageHistory}
      />
      <Route
        exact
        path={Routes.documents.recordOfProcessingActivities.edit}
        component={RoPAPageEdit}
      />
      <Route
        exact
        path={Routes.documents.hrDataRetentionPolicies}
        component={HRDataRetentionPolicyDocuments}
      />
      <Route exact path={Routes.documents.auditLogs} component={AuditLogsPage} />
      <Route exact path={Routes.documents.privacyPolicies} component={PrivacyPolicyDocumentsPage} />
      <Route exact path={Routes.documents.myFiles} component={MyFilesPage} />
      <Route exact path={Routes.invite} component={AcceptInvitation} />
      <Route exact path={Routes.privacyCenter.settings}>
        <Redirect to={Routes.privacyCenter.dsarForms} />
      </Route>
      <Route exact path={Routes.privacyCenter.privacyEmails}>
        <Redirect to={Routes.requests.privacyEmailSettings} />
      </Route>
      <Route exact path={Routes.root}>
        <Redirect to={defaultRedirectRoute} />
      </Route>
      <Route exact path={Routes.login}>
        <Redirect to={Routes.root} />
      </Route>
      <Route exact path={Routes.auth.mfa}>
        <Redirect to={Routes.root} />
      </Route>
      <Route exact path={Routes.auth.reset}>
        <Redirect to={Routes.root} />
      </Route>
      <Route exact path={Routes.auth.set}>
        <Redirect to={Routes.root} />
      </Route>
      <Route path={Routes.setup.root}>
        <Redirect to={Routes.root} />
      </Route>
      <Route path={Routes.getCompliant.root}>
        <Redirect to={Routes.root} />
      </Route>
      <Route exact path={Routes.integrations.shopify} render={() => <ShopifyIntegrationPage />} />
      <FourOhFourRoutes />
    </Switch>
  );
};

const HrAccessDeniedPage = () => {
  const history = useHistory();
  return (
    <div className="tw-boundary">
      <div className="flex flex-col items-center justify-center text-center bg-white rounded-lg shadow-sm border border-gray-200 w-[480px] mx-auto mt-[300px] px-[40px] py-[30px]">
        <LockClosedIcon className="w-12 h-12 text-gray-400 mb-4" aria-hidden="true" />
        <p className="max-w-md text-gray-600 mb-6 leading-relaxed">
          As an HR user, you do not have permission to access this page. Please use the HR Center
          for your tasks.
        </p>
        <Button color="primary" onClick={() => history.push(Routes.hrCenter.root)}>
          Go to HR Center
        </Button>
      </div>
    </div>
  );
};
